import React, {useContext, useEffect, useRef, useState} from "react";
import {
  Box, Button, CircularProgress, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Call02Icon, CallEnd01Icon, HeadsetIcon, Refresh01Icon} from "@hugeicons/core-free-icons";
import {SignInContext} from "../../context/SignInContext";
import {isWebrtcEnabled, useWebrtc, WEBRTC_ROLES} from "../../context/WebrtcContext";
import {useRegistryProject} from "../../pages/registry-project/RegistryProjectContext";
import {DialNumberDialog} from "../../components/webrtc/DialNumberDialog";
import {pbxWebSocketUrl} from "../../pages/backend-configurations/serviceFields";

const buttonSx = {
  height: 32, px: 1.5, borderRadius: "9px", textTransform: "none", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
  minWidth: 0,
};

const STATES = {
  disconnected: {label: "Connect", dot: null},
  connecting: {label: "Connecting…", dot: null},
  verifying: {label: "Checking Asterisk…", dot: null},
  registered: {label: "Connected", dot: "#22C55E"},
  unregistered: {label: "Reconnecting…", dot: "#F59E0B"},
  failed: {label: "Browser calls failed", dot: "#E5484D"},
};

// Header button that turns the browser phone (WebRTC) on and shows its state. Project ADMIN / MANAGER / AGENT only.
// Once turned on it reconnects by itself on the next login / page load (fresh config every time).
export const WebrtcConnectButton = ({role, compact = false}) => {
  const {currentUser} = useContext(SignInContext);
  const {project, subProject} = useRegistryProject();
  const {status, error, connect, disconnect, attachProject} = useWebrtc();
  const [anchor, setAnchor] = useState(null);
  const [dialOpen, setDialOpen] = useState(false);
  const allowed = WEBRTC_ROLES.includes(role);
  // the browser phone connects to the project's backend configuration PBX; without one it cannot be turned on
  const wsUrl = pbxWebSocketUrl(project?.backendConfiguration?.pbx);
  // subProjects: names for the redirect dialog's bot extensions
  const projectInfo = {projectId: project?.id, subProjectId: subProject?.id, wsUrl, subProjects: project?.subProjects || []};

  useEffect(() => {
    if (allowed) attachProject(projectInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, project?.id, subProject?.id, wsUrl]);

  // reconnect once when the user turned it on before
  const autoConnectedRef = useRef(false);
  useEffect(() => {
    if (!allowed || autoConnectedRef.current || !project?.id || !wsUrl || status !== "disconnected") return;
    autoConnectedRef.current = true;
    if (isWebrtcEnabled(currentUser?.username)) connect(projectInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, project?.id, wsUrl, currentUser?.username]);

  if (!allowed) return null;

  const state = STATES[status] || STATES.disconnected;
  const connected = status === "registered";
  const openDial = () => {
    setAnchor(null);
    setDialOpen(true);
  };

  // no PBX in the project's backend configuration: nothing to connect to
  const noPbx = !wsUrl && status === "disconnected";

  const onClick = (e) => {
    if (status === "disconnected") connect(projectInfo);
    else setAnchor(e.currentTarget);
  };

  return (
    <>
      <Tooltip title={noPbx ? "No PBX is set in this project's backend configuration" : ""}>
      <span>
      <Button
        onClick={onClick}
        disabled={noPbx || status === "connecting" || status === "verifying"}
        aria-label={state.label}
        startIcon={status === "connecting" || status === "verifying"
          ? <CircularProgress size={14} sx={{color: "#4B55C9"}}/>
          : <HugeiconsIcon icon={HeadsetIcon} size={15}/>}
        sx={{
          ...buttonSx, border: "1px solid #E4E4EE", bgcolor: "#fff", "&:hover": {bgcolor: "#F4F4FA"},
          color: status === "registered" ? "#1E8A55" : status === "failed" ? "#C73A3A" : "#23264D",
          "&.Mui-disabled": {color: noPbx ? "#A0A0B4" : "#4B55C9"},
          ...(compact ? {"& .MuiButton-startIcon": {mr: 0}} : {}),
        }}
      >
        {compact ? null : state.label}
        {state.dot && (
          <Box component={"span"} sx={{ml: compact ? 0.5 : 1, width: 7, height: 7, borderRadius: "50%", bgcolor: state.dot}}/>
        )}
      </Button>
      </span>
      </Tooltip>
      {connected && (
        <Tooltip title={"Call a number"}>
          <IconButton onClick={openDial} aria-label={"Call a number"} sx={{
            width: 32, height: 32, borderRadius: "9px", color: "#fff", bgcolor: "#1E8A55", "&:hover": {bgcolor: "#18734A"},
          }}>
            <HugeiconsIcon icon={Call02Icon} size={15}/>
          </IconButton>
        </Tooltip>
      )}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        // "Call a number" opens a dialog: focus goes to its phone field, not back to this button
        disableRestoreFocus
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
        transformOrigin={{vertical: "top", horizontal: "right"}}
        PaperProps={{sx: {borderRadius: "12px", mt: 0.75, minWidth: 240, maxWidth: 260, boxShadow: "0 12px 40px rgba(35, 38, 77, 0.16)"}}}
      >
        <Stack sx={{px: 2, py: 1}}>
          <Typography sx={{fontSize: 13, fontWeight: 700, color: "#23264D"}}>{state.label}</Typography>
          <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
            {status === "registered"
              ? "Asterisk is connected to your browser phone. You can take over and place calls."
              : error || "Your browser phone is not registered."}
          </Typography>
        </Stack>
        <Divider sx={{my: 0.5}}/>
        {connected && (
          <MenuItem onClick={openDial} sx={{mx: 0.75, borderRadius: "8px", gap: 1, color: "#1E8A55"}}>
            <ListItemIcon sx={{minWidth: "28px !important", color: "#1E8A55"}}><HugeiconsIcon icon={Call02Icon} size={18}/></ListItemIcon>
            <ListItemText primary={"Call a number"} primaryTypographyProps={{fontSize: 13, fontWeight: 600}}/>
          </MenuItem>
        )}
        {status !== "registered" && (
          <MenuItem onClick={() => {
            setAnchor(null);
            connect(projectInfo);
          }} sx={{mx: 0.75, borderRadius: "8px", gap: 1}}>
            <ListItemIcon sx={{minWidth: "28px !important"}}><HugeiconsIcon icon={Refresh01Icon} size={18}/></ListItemIcon>
            <ListItemText primary={"Reconnect"} primaryTypographyProps={{fontSize: 13, fontWeight: 600}}/>
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          setAnchor(null);
          disconnect();
        }} sx={{mx: 0.75, borderRadius: "8px", gap: 1, color: "#C73A3A"}}>
          <ListItemIcon sx={{minWidth: "28px !important", color: "#C73A3A"}}><HugeiconsIcon icon={CallEnd01Icon} size={18}/></ListItemIcon>
          <ListItemText primary={"Turn off browser calls"} primaryTypographyProps={{fontSize: 13, fontWeight: 600}}/>
        </MenuItem>
      </Menu>
      <DialNumberDialog open={dialOpen} onClose={() => setDialOpen(false)}/>
    </>
  );
};
